import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const HolidayLeadForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    salutation: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    interest_type: '',
    location: '',
    consent_marketing: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - first_name, email required (salutation, phone, last_name optional)
    if (!formData.first_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate phone number format if provided
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 15) {
        toast.error('Please enter a valid phone number (10-15 digits)');
        return;
      }
    }

    if (!formData.consent_marketing) {
      toast.error('Please agree to receive tips and event invites');
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formDataObj = new FormData();
      
      formDataObj.append('salutation', formData.salutation);
      formDataObj.append('first_name', formData.first_name);
      formDataObj.append('last_name', formData.last_name);
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone || '');
      formDataObj.append('interest_type', formData.interest_type || '');
      formDataObj.append('location', formData.location || '');
      formDataObj.append('consent_marketing', formData.consent_marketing);

      const response = await fetch(`${backendUrl}/api/v1/leads/holiday`, {
        method: 'POST',
        body: formDataObj
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Success! Check your email for the download link.');
        navigate(`/holiday/thank-you?email=${encodeURIComponent(formData.email)}`);
      } else {
        toast.error(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto rounded-2xl p-8 border border-white/15 bg-white/10 backdrop-blur-xl shadow-[0_12px_40px_rgba(11,34,64,.24)]"
      data-testid="holiday-lead-form"
    >
      {/* Form Heading */}
      <div className="text-center mb-6">
        <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-white mb-2">
          Holiday Bonus Form
        </h3>
        <p className="text-sm text-white/80">
          Complete the form below to enter the $1,000 Visa® Holiday Gift Card Giveaway and receive your Holiday Money-Saver Kit by email.
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Salutation */}
        <div>
          <Label htmlFor="salutation" className="text-white font-medium mb-2 block">
            Salutation
          </Label>
          <Select data-testid="holiday-lead-form-select-2"
            value={formData.salutation}
            onValueChange={(value) => setFormData({ ...formData, salutation: value })}
          >
            <SelectTrigger 
              id="salutation"
              className="bg-white/10 border-white/20 text-white h-12 focus:border-[#D4AF37]"
              data-testid="salutation-select"
            >
              <SelectValue placeholder="Select title (optional)..." className="text-white/50" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="holiday-lead-form-selectitem-8" value="Mr">Mr</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-7" value="Mrs">Mrs</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-6" value="Ms">Ms</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-5" value="Dr">Dr</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* First Name */}
        <div>
          <Label htmlFor="first_name" className="text-white font-medium mb-2 block">
            First name *
          </Label>
          <Input
            id="first_name"
            type="text"
            required
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] h-12"
            placeholder="John"
            data-testid="first-name-input"
          />
        </div>

        {/* Last Name */}
        <div>
          <Label htmlFor="last_name" className="text-white font-medium mb-2 block">
            Last name
          </Label>
          <Input
            id="last_name"
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] h-12"
            placeholder="Smith"
            data-testid="last-name-input"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="text-white font-medium mb-2 block">
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] h-12"
            placeholder="john@example.com"
            data-testid="email-input"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone" className="text-white font-medium mb-2 block">
            Phone
          </Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              // Only allow numbers, spaces, dashes, parentheses, and plus sign
              const cleaned = e.target.value.replace(/[^0-9\s\-\(\)\+]/g, '');
              // Limit to 20 characters (enough for international format)
              if (cleaned.length <= 20) {
                setFormData({ ...formData, phone: cleaned });
              }
            }}
            maxLength={20}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] h-12"
            placeholder="(555) 123-4567"
            data-testid="phone-input"
          />
          <p className="text-xs text-white/50 mt-1">Format: (555) 123-4567 or 555-123-4567</p>
        </div>

        {/* Interest Type */}
        <div>
          <Label className="text-white font-medium mb-3 block">
            I'm most interested in...
          </Label>
          <RadioGroup
            value={formData.interest_type}
            onValueChange={(value) => setFormData({ ...formData, interest_type: value })}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
              <RadioGroupItem value="Business Owner" id="business" className="border-white/40 text-[#D4AF37]" />
              <Label htmlFor="business" className="text-white font-normal cursor-pointer flex-1">
                Business Owner
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
              <RadioGroupItem value="Individual/Family" id="individual" className="border-white/40 text-[#D4AF37]" />
              <Label htmlFor="individual" className="text-white font-normal cursor-pointer flex-1">
                Individual/Family
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Location */}
        <div>
          <Label htmlFor="location" className="text-white font-medium mb-2 block">
            I'm near:
          </Label>
          <Select data-testid="holiday-lead-form-select-1" value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white h-12" data-testid="location-select">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="holiday-lead-form-selectitem-4" value="Winston-Salem">Winston-Salem</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-3" value="Charlotte">Charlotte</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-2" value="Mooresville">Mooresville</SelectItem>
              <SelectItem data-testid="holiday-lead-form-selectitem-1" value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Consent Checkbox */}
        <div className="flex items-start space-x-3 p-4 rounded-lg bg-white/5">
          <Checkbox
            id="consent"
            checked={formData.consent_marketing}
            onCheckedChange={(checked) => setFormData({ ...formData, consent_marketing: checked })}
            className="mt-1 border-white/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
            data-testid="consent-checkbox"
          />
          <Label htmlFor="consent" className="text-white/90 font-normal leading-relaxed cursor-pointer">
            Yes, send me helpful tips and event invites.
          </Label>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-14 text-lg rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] hover:brightness-95 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] focus-visible:ring-2 focus-visible:ring-[#D4AF37] font-semibold"
          data-testid="submit-button"
        >
          {loading ? 'Submitting...' : 'Enter Now + Get Your Kit'}
        </Button>

        {/* Disclaimer */}
        <p className="text-xs text-white/60 leading-relaxed text-center">
          By submitting, you agree to receive email updates from KGOB CPAs & Advisors (and occasional invitations to use our tools like LetsTalkTaxes.io). No purchase necessary. Void where prohibited. Visa® is not a sponsor or affiliate of this giveaway.
        </p>
      </div>
    </form>
  );
};
